<script setup>
import {computed, onMounted, ref} from "vue";

// ponytail: all pricing assumptions live here — tune freely, no backend involved
const RETAIL = {basic: 19, essentials: 49, pro: 99}; // public per-seat retail (config/billing.php)
const SERVER_COST = {basic: 1, essentials: 1.25, pro: 1.5}; // infrastructure cost €/user/month
const AI_ALLOWANCE = {basic: 2, essentials: 5, pro: 20}; // AI budget per seat €/month (AiUsageTracker::monthlyLimit)
const MARGIN_PER_USER = 3.5; // flat myflow margin €/user/month
const COMMISSION_RATE = 0.10; // partner seller commission, grossed up into the seat price
const SUPPORT_PER_USER = 5; // white-labeled support premium €/user/month
const EXCLUSIVITY_FEE = 25000; // country-specific exclusivity €/month
const MOBILE_APPS_MONTHLY = 2500; // white-labeled iOS/Android apps €/month maintenance
const MOBILE_APPS_SETUP = 10000; // one-time setup fee, not part of the monthly total
const MIN_TOTAL_USERS = 250; // myflow-defined minimum customer count
const MIN_MONTHLY_BILL = 25000; // minimum monthly commitment €

const MODELS = [
    {
        key: 'embedded',
        name: 'Embedded',
        emoji: '🧩',
        description: 'Embed myflow features directly inside your existing product with your own look and feel.',
    },
    {
        key: 'api',
        name: 'API',
        emoji: '⚡',
        description: 'Build on top of the full myflow API and keep complete control over your own frontend.',
    },
    {
        key: 'brand',
        name: 'New brand',
        emoji: '🚀',
        description: 'The complete platform relaunched under your own brand, domain and identity.',
    },
];

const TIERS = [
    {key: 'basic', emoji: '🌱'},
    {key: 'essentials', emoji: '🌿'},
    {key: 'pro', emoji: '🌳'},
];

const model = ref('brand');
const seats = ref({basic: 100, essentials: 100, pro: 50});
const whitelabelSupport = ref(false);
const exclusivity = ref(false);
const mobileApps = ref(false);

const transitioned = ref(false);
onMounted(() => setTimeout(() => transitioned.value = true, 100));

// cost-plus: (baseline cost + flat margin) grossed up so the 10% commission doesn't eat the margin
const seatPrice = (tier) =>
    Math.round(((SERVER_COST[tier] + AI_ALLOWANCE[tier] + MARGIN_PER_USER) / (1 - COMMISSION_RATE)) * 100) / 100;

const totalUsers = computed(() => seats.value.basic + seats.value.essentials + seats.value.pro);

const seatSubtotal = computed(() =>
    seats.value.basic * seatPrice('basic')
    + seats.value.essentials * seatPrice('essentials')
    + seats.value.pro * seatPrice('pro')
);

const supportCost = computed(() => whitelabelSupport.value ? totalUsers.value * SUPPORT_PER_USER : 0);

const exclusivityCost = computed(() => exclusivity.value ? EXCLUSIVITY_FEE : 0);

const mobileAppsCost = computed(() => mobileApps.value ? MOBILE_APPS_MONTHLY : 0);

// exclusivity is charged on top of the minimum, so it never counts toward reaching the floor
const rawTotal = computed(() => seatSubtotal.value + supportCost.value + mobileAppsCost.value);

const belowMinimum = computed(() => rawTotal.value < MIN_MONTHLY_BILL);
const total = computed(() => Math.max(rawTotal.value, MIN_MONTHLY_BILL) + exclusivityCost.value);

const euro = (n) => '€' + n.toLocaleString('en-US', {maximumFractionDigits: 2}).replaceAll(',', ' ');
</script>

<template>
    <div class="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
        <!-- Top bar -->
        <div class="flex items-center justify-between px-4 py-3 mx-auto max-w-6xl">
            <img
                src="https://myflow.fra1.digitaloceanspaces.com/myflow-brand/logo-purple.svg"
                alt="myflow"
                class="w-24"
            />
        </div>

        <div class="max-w-6xl px-4 pb-24 mx-auto">
            <!-- Hero -->
            <div class="pt-10 text-center md:pt-16 wl-appear">
                <p class="text-xs font-semibold tracking-widest uppercase text-myflowPurple">
                    White label partnership
                </p>
                <h1 class="mt-3 text-3xl font-bold text-gray-900 md:text-5xl" style="line-height: 1.15em;">
                    myflow, under your brand
                </h1>
                <p class="max-w-lg mx-auto mt-5 text-lg font-light text-gray-700">
                    Embedded, via API, or as a completely new brand. Build your package below and watch your monthly investment update live.
                </p>
            </div>

            <div class="mt-12 lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">
                <div class="lg:col-span-2 space-y-8">
                    <!-- Deployment model -->
                    <div class="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl md:p-8 wl-appear" style="animation-delay: 0.05s">
                        <p class="text-xs font-semibold tracking-widest uppercase text-myflowPurple">Step 1</p>
                        <h2 class="mt-1 text-2xl font-bold text-gray-900">Choose your deployment model</h2>
                        <p class="mt-1 mb-6 font-light text-gray-700">How do you want to bring myflow to your customers?</p>

                        <div class="grid gap-4 md:grid-cols-3">
                            <div
                                v-for="m in MODELS"
                                :key="m.key"
                                class="p-5 transition-all duration-200 border-2 cursor-pointer rounded-2xl hover:-translate-y-0.5 hover:shadow-md"
                                :class="model === m.key ? 'border-myflowPurple bg-myflowPurple/5 shadow-md' : 'border-gray-100 bg-white'"
                                @click="model = m.key"
                            >
                                <span class="text-3xl">{{ m.emoji }}</span>
                                <p class="mt-2 font-bold text-gray-900">{{ m.name }}</p>
                                <p class="mt-1 text-sm text-gray-600">{{ m.description }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Seats -->
                    <div class="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl md:p-8 wl-appear" style="animation-delay: 0.1s">
                        <p class="text-xs font-semibold tracking-widest uppercase text-myflowPurple">Step 2</p>
                        <h2 class="mt-1 text-2xl font-bold text-gray-900">Build your package</h2>
                        <p class="mt-1 mb-6 font-light text-gray-700">
                            How many customers do you want to include on each plan?
                            myflow requires a minimum of <strong class="font-semibold">{{ MIN_TOTAL_USERS }} customers</strong> to get started.
                        </p>

                        <div class="grid gap-6 md:grid-cols-3">
                            <div
                                v-for="tier in TIERS"
                                :key="tier.key"
                                class="p-5 text-center border border-gray-100 rounded-2xl bg-gray-50/50"
                            >
                                <span class="text-2xl">{{ tier.emoji }}</span>
                                <p class="mt-1 font-bold text-gray-900 capitalize">{{ tier.key }}</p>
                                <p class="mt-1 mb-4 text-sm text-gray-600">
                                    <span class="font-semibold text-myflowPurple">{{ euro(seatPrice(tier.key)) }}</span> / customer / month
                                    <span class="block mt-0.5 text-xs text-gray-400">retail {{ euro(RETAIL[tier.key]) }}</span>
                                </p>
                                <input-number
                                    min="0"
                                    max="100000"
                                    :step="10"
                                    class="mx-auto w-36"
                                    v-model:content="seats[tier.key]"
                                    :show-step-ui="true"
                                />
                            </div>
                        </div>

                        <div
                            v-if="totalUsers < MIN_TOTAL_USERS"
                            class="p-4 mt-6 text-sm rounded-2xl bg-amber-50 text-amber-800"
                        >
                            <i class="mr-1 fa-regular fa-triangle-exclamation"></i>
                            You currently have {{ totalUsers }} customers. Add at least {{ MIN_TOTAL_USERS - totalUsers }} more to reach the minimum of {{ MIN_TOTAL_USERS }}.
                        </div>
                    </div>

                    <!-- Support -->
                    <div class="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl md:p-8 wl-appear" style="animation-delay: 0.15s">
                        <p class="text-xs font-semibold tracking-widest uppercase text-myflowPurple">Step 3</p>
                        <h2 class="mt-1 text-2xl font-bold text-gray-900">Support</h2>
                        <p class="mt-1 mb-6 font-light text-gray-700">Who handles support for your customers?</p>

                        <div class="grid gap-4 md:grid-cols-2">
                            <div
                                class="p-5 transition-all duration-200 border-2 cursor-pointer rounded-2xl hover:-translate-y-0.5 hover:shadow-md"
                                :class="!whitelabelSupport ? 'border-myflowPurple bg-myflowPurple/5 shadow-md' : 'border-gray-100 bg-white'"
                                @click="whitelabelSupport = false"
                            >
                                <span class="text-3xl">🤝</span>
                                <p class="mt-2 font-bold text-gray-900">Self-managed</p>
                                <p class="mt-1 mb-3 text-sm text-gray-600">Your team handles all customer support. We support you, you support them.</p>
                                <p class="font-bold text-myflowPurple">Included</p>
                            </div>
                            <div
                                class="p-5 transition-all duration-200 border-2 cursor-pointer rounded-2xl hover:-translate-y-0.5 hover:shadow-md"
                                :class="whitelabelSupport ? 'border-myflowPurple bg-myflowPurple/5 shadow-md' : 'border-gray-100 bg-white'"
                                @click="whitelabelSupport = true"
                            >
                                <span class="text-3xl">🎧</span>
                                <p class="mt-2 font-bold text-gray-900">White-labeled support</p>
                                <p class="mt-1 mb-3 text-sm text-gray-600">Our support team answers your customers under your brand name.</p>
                                <p class="font-bold text-myflowPurple">{{ euro(SUPPORT_PER_USER) }} <span class="text-sm font-normal text-gray-500">/ customer / month</span></p>
                            </div>
                        </div>
                    </div>

                    <!-- Exclusivity -->
                    <div class="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl md:p-8 wl-appear" style="animation-delay: 0.2s">
                        <p class="text-xs font-semibold tracking-widest uppercase text-myflowPurple">Step 4</p>
                        <h2 class="mt-1 text-2xl font-bold text-gray-900">Country exclusivity</h2>
                        <p class="mt-1 mb-6 font-light text-gray-700">Become the only white-label partner in your country.</p>

                        <div
                            class="p-5 transition-all duration-200 border-2 cursor-pointer rounded-2xl hover:-translate-y-0.5 hover:shadow-md"
                            :class="exclusivity ? 'border-myflowPurple bg-myflowPurple/5 shadow-md' : 'border-gray-100 bg-white'"
                            @click="exclusivity = !exclusivity"
                        >
                            <div class="flex items-center justify-between gap-4">
                                <div class="flex items-start gap-4">
                                    <span class="text-3xl">👑</span>
                                    <div>
                                        <p class="font-bold text-gray-900">Country-specific exclusivity</p>
                                        <p class="mt-1 text-sm text-gray-600">We won't sign any other white-label partner in your country while your agreement is active. Charged on top of the minimum monthly commitment.</p>
                                    </div>
                                </div>
                                <p class="font-bold whitespace-nowrap text-myflowPurple">{{ euro(EXCLUSIVITY_FEE) }} <span class="text-sm font-normal text-gray-500">/ month</span></p>
                            </div>
                        </div>
                    </div>

                    <!-- Mobile apps -->
                    <div class="p-6 bg-white border border-gray-100 shadow-sm rounded-2xl md:p-8 wl-appear" style="animation-delay: 0.25s">
                        <p class="text-xs font-semibold tracking-widest uppercase text-myflowPurple">Step 5</p>
                        <h2 class="mt-1 text-2xl font-bold text-gray-900">Mobile apps</h2>
                        <p class="mt-1 mb-6 font-light text-gray-700">Your own branded apps in the App Store and Google Play.</p>

                        <div
                            class="p-5 transition-all duration-200 border-2 cursor-pointer rounded-2xl hover:-translate-y-0.5 hover:shadow-md"
                            :class="mobileApps ? 'border-myflowPurple bg-myflowPurple/5 shadow-md' : 'border-gray-100 bg-white'"
                            @click="mobileApps = !mobileApps"
                        >
                            <div class="flex items-center justify-between gap-4">
                                <div class="flex items-start gap-4">
                                    <span class="text-3xl">📱</span>
                                    <div>
                                        <p class="font-bold text-gray-900">White-labeled iOS &amp; Android apps</p>
                                        <p class="mt-1 text-sm text-gray-600">We build, publish and maintain the apps under your brand — updates and store compliance included.</p>
                                    </div>
                                </div>
                                <p class="font-bold text-right whitespace-nowrap text-myflowPurple">
                                    {{ euro(MOBILE_APPS_MONTHLY) }} <span class="text-sm font-normal text-gray-500">/ month</span>
                                    <span class="block text-sm font-normal text-gray-500">+ {{ euro(MOBILE_APPS_SETUP) }} one-time setup</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Summary: always visible in right column -->
                <div class="mt-8 lg:mt-0 lg:sticky lg:top-8 wl-appear" style="animation-delay: 0.3s">
                    <div class="overflow-hidden bg-white border border-gray-100 shadow-lg rounded-2xl">
                        <div class="h-1.5 w-full bg-gradient-to-r from-myflowPurple via-pink-400 to-myflowPurple"></div>
                        <div class="p-6 md:p-8">
                            <p class="text-xs font-semibold tracking-widest uppercase text-myflowPurple">Your package</p>
                            <h2 class="mt-1 mb-4 text-2xl font-bold text-gray-900">Monthly investment</h2>

                            <div class="text-center py-4">
                                <p class="text-4xl font-bold text-myflowPurple total-pop" :key="total">{{ euro(total) }}</p>
                                <p class="mt-1 text-sm text-gray-500">per month</p>
                            </div>

                            <div class="mt-2 text-sm divide-y divide-gray-100">
                                <div class="flex justify-between py-3">
                                    <span class="text-gray-600">Deployment</span>
                                    <span class="font-semibold text-gray-900">{{ MODELS.find(m => m.key === model).name }}</span>
                                </div>
                                <div class="flex justify-between py-3">
                                    <span class="text-gray-600">{{ totalUsers.toLocaleString('en-US').replaceAll(',', ' ') }} customer seats</span>
                                    <span class="font-semibold text-gray-900">{{ euro(seatSubtotal) }}</span>
                                </div>
                                <div class="flex justify-between py-3">
                                    <span class="text-gray-600">Support ({{ whitelabelSupport ? 'white-labeled' : 'self-managed' }})</span>
                                    <span class="font-semibold text-gray-900">{{ supportCost ? euro(supportCost) : 'Included' }}</span>
                                </div>
                                <div v-if="mobileApps" class="flex justify-between py-3">
                                    <span class="text-gray-600">Mobile apps</span>
                                    <span class="font-semibold text-gray-900">{{ euro(mobileAppsCost) }}</span>
                                </div>
                                <div v-if="belowMinimum" class="flex justify-between py-3 text-amber-700">
                                    <span>Minimum monthly commitment</span>
                                    <span class="font-semibold">{{ euro(MIN_MONTHLY_BILL) }}</span>
                                </div>
                                <div v-if="exclusivity" class="flex justify-between py-3">
                                    <span class="text-gray-600">Country exclusivity</span>
                                    <span class="font-semibold text-gray-900">{{ euro(exclusivityCost) }}</span>
                                </div>
                                <div v-if="mobileApps" class="flex justify-between py-3 text-gray-500">
                                    <span>One-time mobile app setup</span>
                                    <span>{{ euro(MOBILE_APPS_SETUP) }}</span>
                                </div>
                            </div>

                            <p
                                v-if="belowMinimum"
                                class="p-4 mt-4 text-sm rounded-2xl bg-amber-50 text-amber-800"
                            >
                                Your configuration adds up to {{ euro(rawTotal) }}, below the {{ euro(MIN_MONTHLY_BILL) }} minimum
                                to get started. Add more customers or options to make the most of your commitment.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes wl-appear {
    from {
        opacity: 0;
        transform: translateY(8px) scale(0.97);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}
.wl-appear {
    animation: wl-appear 0.4s ease-out both;
}

@keyframes total-pop {
    0% {
        transform: scale(0.94);
    }
    60% {
        transform: scale(1.04);
    }
    100% {
        transform: scale(1);
    }
}
.total-pop {
    animation: total-pop 0.3s ease-out;
}
</style>
